import React, { useEffect, useRef, useState } from 'react'
import {
  AlertBox,
  Button,
  CustomLinkComponent,
  Field,
  Grid,
  Icons,
  PasswordSecurity,
  Spinner
} from '../../../index'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import {
  useCustomer,
  useNavigation,
  useConfig,
  useCookie
} from 'msp-integrations'
import getRegisterFields from './config/RegisterForm.fields'
import { messages, validateBirthDay } from './RegisterForm.utils'
import * as T from './RegisterForm.types'

const RegisterForm = ({
  className,
  fromCheckout,
  redirectPath,
  onClickLink,
  onRegister,
  setLoadingState
}: T.RegisterFormProps) => {
  const fieldConfig = getRegisterFields(fromCheckout || false)
  const redirectUrl = redirectPath || '/account'

  const {
    control,
    errors,
    formState,
    handleSubmit,
    setError,
    clearErrors,
    watch
  } = useForm()
  const {
    API_ERROR_MESSAGE,
    theme: { font }
  } = useConfig()
  const { eventTracking } = useCookie()
  const watchPwdField = watch(fieldConfig.password.name)
  const { formatMessage } = useIntl()
  const customer = useCustomer()
  const navigate = useNavigation()
  const alertBoxRef = useRef<typeof AlertBox>()
  const [passwordStrength, setPasswordStrength] = useState<number>(0)

  useEffect(() => {
    eventTracking('registration', { event_action: 'start' })
  }, [])

  const onFormSubmit = async (data: T.HandleSubmitProps) => {
    const { newsletter, ...rest } = data
    if (setLoadingState) {
      setLoadingState(true)
    }

    let requestData = rest
    try {
      if (data.password !== data.repeatPassword) {
        setError(fieldConfig.password.name, {
          type: 'manual',
          message: formatMessage(messages.password_match)
        })

        setError(fieldConfig.repeatPassword.name, {
          type: 'manual',
          message: formatMessage(messages.password_match)
        })

        eventTracking('registration_failed', {
          pageType: fromCheckout ? 'checkout' : 'registration',
          reason: formatMessage(messages.password_match)
        })
        return
      }

      if (passwordStrength < 4) {
        setError(fieldConfig.password.name, {
          type: 'manual',
          message: formatMessage(messages.requirement)
        })

        setError(fieldConfig.repeatPassword.name, {
          type: 'manual',
          message: formatMessage(messages.requirement)
        })

        eventTracking('registration_failed', {
          pageType: fromCheckout ? 'checkout' : 'registration',
          reason: formatMessage(messages.requirement)
        })
        return
      }

      if (data.birthday) {
        const isValidBirthdate = validateBirthDay(data.birthday)

        if (!isValidBirthdate) {
          setError(fieldConfig.birthday.name, {
            type: 'manual',
            message: formatMessage(messages.invalid)
          })

          eventTracking('registration_failed', {
            pageType: fromCheckout ? 'checkout' : 'registration',
            reason: formatMessage(messages.invalid)
          })
          return
        }

        if (isValidBirthdate) {
          requestData = {
            ...requestData,
            birthday: data.birthday
          }
        }
      }

      await customer.registerCustomer({
        ...requestData,
        newsletter,
        ...(newsletter && { newsletterSource: 'register' })
      })

      //registerCustomer method already logs in after registration
      eventTracking('registration_succeeded', {
        pageType: fromCheckout ? 'checkout' : 'registration',
        checked_newsletter: newsletter ? 'yes' : 'no'
      })

      if (onRegister) {
        onRegister()
      }

      navigate(redirectUrl)
    } catch (error: any) {
      setError('global', { type: 'manual', message: error.message })
      eventTracking('registration_failed', {
        pageType: fromCheckout ? 'checkout' : 'registration',
        reason: error.message
      })
      alertBoxRef?.current?.showAlert()
    } finally {
      if (setLoadingState) {
        setLoadingState(false)
      }
    }
  }

  const getButtons = (fromCheckout: boolean) => {
    if (fromCheckout) {
      return (
        <div className='mt-8 flex flex-col items-center justify-center'>
          <Button
            className='w-full md:w-auto'
            onClick={() => clearErrors()}
            isDisabled={formState.isSubmitting}
            isLoading={formState.isSubmitting}
            isSubmit
            loadingContent={<Spinner size='xl' />}
          >
            {formatMessage(messages.buttonRegisterCheckout)}
          </Button>
          <button
            className='mt-6'
            onClick={() => onClickLink && onClickLink('login')}
          >
            <CustomLinkComponent
              className='text-10 tracking-3'
              colorScheme='primary'
            >
              {formatMessage(messages.buttonLoginCheckout)}
            </CustomLinkComponent>
          </button>
        </div>
      )
    }

    return (
      <Button
        className='w-full md:w-6/12'
        onClick={() => clearErrors()}
        iconName={!formState.isSubmitting ? 'ArrowRight' : ''}
        iconPosition='right'
        isDisabled={formState.isSubmitting}
        isLoading={formState.isSubmitting}
        isSubmit
        loadingContent={<Spinner size='xl' />}
      >
        {formatMessage(messages.button)}
      </Button>
    )
  }

  return (
    <div className={`${font.regular} mt-6 ${className}`}>
      <AlertBox
        ref={alertBoxRef}
        description={
          errors?.global &&
          ((errors?.global.message === 'EMAIL_ALREADY_EXISTS' &&
            formatMessage(messages.existing_account)) ||
            formatMessage(API_ERROR_MESSAGE))
        }
        closeButtonIcon={<Icons iconName='Close' size='base' />}
      />
      <form onSubmit={handleSubmit(onFormSubmit)} id={'register-form'}>
        {!fromCheckout && (
          <>
            <div className='mb-2'>
              <Field
                control={control}
                config={fieldConfig.salutation}
                error={errors?.salutation}
                isDisabled={formState.isSubmitting}
              />
            </div>
          </>
        )}
        <Grid wrapperStyles='grid-cols-1 md:grid-cols-2 md:gap-2'>
          <Field
            className={'w-full'}
            control={control}
            config={fieldConfig.firstName}
            error={errors?.firstName}
            isDisabled={formState.isSubmitting}
          />
          <Field
            className={'w-full'}
            control={control}
            config={fieldConfig.lastName}
            error={errors?.lastName}
            isDisabled={formState.isSubmitting}
          />
        </Grid>
        <Field
          className={'w-full'}
          control={control}
          config={fieldConfig.email}
          error={errors?.email}
          isDisabled={formState.isSubmitting}
        />
        <Field
          className={'w-full'}
          control={control}
          config={fieldConfig.password}
          error={errors?.password}
          isDisabled={formState.isSubmitting}
        />
        <Field
          className={'w-full'}
          control={control}
          config={fieldConfig.repeatPassword}
          error={errors?.repeatPassword}
          isDisabled={formState.isSubmitting}
        />
        <PasswordSecurity
          password={watchPwdField}
          setStrength={setPasswordStrength}
        />
        {!fromCheckout && (
          <>
            <Field
              className='w-full cursor-pointer'
              control={control}
              config={fieldConfig.birthday}
              error={errors?.birthday}
              isDisabled={formState.isSubmitting}
              additionalProps={{ max: '9999-12-31' }}
            />
            <Field
              className='w-full'
              control={control}
              config={fieldConfig.newsletter}
              error={errors?.newsletter}
              isDisabled={formState.isSubmitting}
            />
          </>
        )}
        <Field
          className='mb-6 w-full'
          control={control}
          config={fieldConfig.dataProtection}
          error={errors?.dataProtection}
          isDisabled={formState.isSubmitting}
        />
        {getButtons(!!fromCheckout)}
      </form>
    </div>
  )
}

export default RegisterForm
